import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { PasswordFields, passwordProblem } from "./PasswordFields";
import { Key } from "@phosphor-icons/react";
import { toast } from "sonner";

// Platform console: set a user's password directly. `endpoint` is POSTed {password, force_change}.
export function SetPasswordDialog({ open, onClose, endpoint, person, onDone }) {
  const [password, setPassword] = useState("");
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const close = () => { setPassword(""); setForceChange(true); setDone(null); onClose(); };

  const submit = async () => {
    if (passwordProblem(password)) { toast.error(passwordProblem(password)); return; }
    setBusy(true);
    try {
      const { data } = await API.post(endpoint, { password, force_change: forceChange });
      setDone(data);
      toast.success(`Password set for ${person?.email || "the user"}`);
      onDone?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't set the password"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-[520px]" data-testid="set-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Key size={18} /> Set password</DialogTitle>
          <DialogDescription>
            {person ? <>For <strong>{person.name || person.email}</strong> ({person.email}). </> : null}
            They are signed out everywhere and we e-mail them a notice — the password itself is never e-mailed.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 p-3 text-xs space-y-1" data-testid="set-password-result">
            <p className="font-medium text-sm text-emerald-900 dark:text-emerald-200">Password set</p>
            <p className="font-mono text-sm break-all" data-testid="set-password-result-value">{password}</p>
            <p className="text-muted-foreground">Share it with them directly. {done.force_password_change ? "They'll choose their own password at next sign-in." : ""} {done.email_sent ? "Security notice e-mailed." : "The notice e-mail could not be sent."}</p>
            {done.login_url && <p className="text-muted-foreground">Sign-in page: <span className="font-mono">{done.login_url}</span></p>}
          </div>
        ) : (
          <PasswordFields value={password} onChange={setPassword} forceChange={forceChange} onForceChange={setForceChange} idPrefix="set-password" />
        )}
        <DialogFooter>
          {done ? <Button onClick={close} data-testid="set-password-done">Done</Button> : (
            <>
              <Button variant="outline" onClick={close} data-testid="set-password-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy || !password || !!passwordProblem(password)} data-testid="set-password-submit">{busy ? "Saving…" : "Set password"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
